import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Tag, ChevronDown, Search, X } from 'lucide-react';

const PANEL_Z = 10060;

/**
 * Placeholder picker for canvas toolbar — menu portals to document.body so it stays
 * above Konva layers and is not clipped by canvas overflow.
 */
export default function PlaceholderFieldDropdown({
  open,
  onOpenChange,
  options = [],
  fieldKey,
  displayLabel,
  onSelect,
  onClear,
  isImageStack = false,
  className = '',
}) {
  const [search, setSearch] = useState('');
  const [panelStyle, setPanelStyle] = useState(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  const filtered = options.filter((opt) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      String(opt.label || '').toLowerCase().includes(q) ||
      String(opt.key || '').toLowerCase().includes(q)
    );
  });

  useLayoutEffect(() => {
    if (!open || !btnRef.current) {
      setPanelStyle(null);
      return undefined;
    }

    function updatePosition() {
      const btn = btnRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const panelW = Math.min(280, Math.max(220, rect.width), window.innerWidth - 16);
      const maxH = Math.min(340, window.innerHeight - 16);
      const gap = 6;
      const estH = Math.min(maxH, 300);

      let left = rect.left;
      let top = rect.bottom + gap;
      let opensUp = false;

      if (top + estH > window.innerHeight - 8) {
        const above = rect.top - gap - estH;
        if (above >= 8 || rect.top > window.innerHeight - rect.bottom) {
          top = Math.max(8, above);
          opensUp = true;
        }
      }

      if (left + panelW > window.innerWidth - 8) {
        left = window.innerWidth - 8 - panelW;
      }
      left = Math.max(8, left);

      setPanelStyle({ left, top, width: panelW, maxHeight: maxH, opensUp });
    }

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, search, options.length]);

  useEffect(() => {
    if (!open) return undefined;
    function handleMouseDown(e) {
      if (btnRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      onOpenChange(false);
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open, onOpenChange]);

  const panel =
    open && panelStyle
      ? createPortal(
          <div
            ref={panelRef}
            className="rounded-lg border border-slate-200 bg-white shadow-xl flex flex-col overflow-hidden"
            style={{
              position: 'fixed',
              left: panelStyle.left,
              top: panelStyle.top,
              width: panelStyle.width,
              maxHeight: panelStyle.maxHeight,
              zIndex: PANEL_Z,
            }}
            role="listbox"
            aria-label="Placeholder fields"
          >
            <div className="px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-100 shrink-0">
              Placeholder
            </div>
            <div className="px-2 py-2 border-b border-slate-100 shrink-0">
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                  aria-hidden
                />
                <input
                  ref={searchRef}
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search placeholders…"
                  className="w-full pl-7 pr-7 py-1.5 text-xs rounded-md border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400"
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      e.stopPropagation();
                      onOpenChange(false);
                    }
                  }}
                />
                {search ? (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                    aria-label="Clear search"
                  >
                    <X size={14} />
                  </button>
                ) : null}
              </div>
            </div>
            <div className="overflow-y-auto overscroll-contain min-h-0 flex-1 py-0.5">
              {filtered.length === 0 ? (
                <p className="px-3 py-4 text-xs text-slate-500 text-center">No placeholders match your search.</p>
              ) : (
                filtered.map((opt) => {
                  const picked = fieldKey === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      role="option"
                      aria-selected={picked}
                      onClick={() => {
                        onSelect(opt.key);
                        onOpenChange(false);
                      }}
                      className={`w-full px-2.5 py-2 text-left flex items-start gap-2 transition-colors border-l-[3px] ${
                        picked
                          ? 'bg-indigo-50 border-indigo-600 text-indigo-900'
                          : 'border-transparent hover:bg-slate-50 text-slate-700'
                      }`}
                    >
                      <Tag size={14} className={`shrink-0 mt-px ${picked ? 'text-indigo-600' : 'text-slate-400'}`} />
                      <span className="block text-xs font-semibold leading-snug">{opt.label}</span>
                      {picked ? (
                        <span className="shrink-0 text-indigo-600 text-sm font-bold" aria-hidden>
                          ✓
                        </span>
                      ) : null}
                    </button>
                  );
                })
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                onClear();
                onOpenChange(false);
              }}
              className="shrink-0 w-full px-2.5 py-1.5 text-left text-xs text-slate-500 hover:bg-slate-100 border-t border-slate-200 bg-white"
            >
              Clear placeholder
            </button>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <div className={className}>
        <button
          ref={btnRef}
          type="button"
          onClick={() => onOpenChange(!open)}
          className={`inline-flex max-w-full items-center gap-1.5 pl-1.5 pr-1.5 py-1 rounded-md text-left transition-colors ${
            isImageStack ? 'w-full justify-between' : ''
          } ${
            fieldKey
              ? 'bg-slate-100/95 text-slate-500 border border-slate-200/90 hover:bg-slate-100'
              : 'text-slate-600 hover:bg-slate-100 border border-dashed border-slate-300'
          }`}
          title={fieldKey ? displayLabel || fieldKey : 'Choose a placeholder field'}
          aria-expanded={open}
          aria-haspopup="listbox"
        >
          <Tag size={13} className={`shrink-0 ${fieldKey ? 'text-slate-400' : 'text-slate-500'}`} />
          <span className="text-[11px] font-medium truncate min-w-0">{displayLabel || 'Placeholder'}</span>
          <ChevronDown
            size={13}
            className={`shrink-0 transition-transform text-slate-400 ${open ? 'rotate-180' : ''}`}
          />
        </button>
      </div>
      {panel}
    </>
  );
}
